/** Recruitment Network · "Add candidate" — adds people to the focused requirement without leaving
 *  the graph. Two ways in, the same two endpoints the Candidates page and requirement page use:
 *
 *  - Upload CVs  → POST /requisitions/{id}/resumes    (each AI-scored; uses one resume scan each)
 *  - Name + email → POST /requisitions/{id}/candidates (sends the AI interview link; one interview)
 *
 *  Both are billable, so both go through the shared ConfirmChargeModal first (pay-before-value):
 *  the recruiter sees what their plan has left before anything is spent. */
import { useRef, useState } from "react";
import type { ChangeEvent, DragEvent, FormEvent, ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UploadCloud, UserPlus, X, FileText, Trash2 } from "lucide-react";
import { api } from "../../../lib/api.js";
import { ConfirmChargeModal } from "../ConfirmChargeModal.js";
import { serverMessage } from "../planUsage.js";
import { displayTitle } from "../displayTitle.js";

type Tab = "upload" | "direct";

/** Server limits (resumeUploadSchema): 20 files per request, 10 MB each. */
const MAX_FILES = 20;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".pdf,.doc,.docx,.txt,application/pdf,text/plain";

async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export function AddCandidateModal({
  reqId,
  reqTitle,
  initialFiles,
  onClose,
}: {
  reqId: string;
  reqTitle: string;
  /** Resumes dropped on the network page before the modal was open. */
  initialFiles?: File[];
  onClose: () => void;
}): ReactNode {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("upload");
  const [files, setFiles] = useState<File[]>(() => (initialFiles ?? []).filter((f) => f.size <= MAX_BYTES).slice(0, MAX_FILES));
  const [fileNote, setFileNote] = useState<string | null>(() => {
    const dropped = initialFiles ?? [];
    const big = dropped.filter((f) => f.size > MAX_BYTES).length;
    return big ? `${big} file${big === 1 ? " is" : "s are"} over 10 MB and ${big === 1 ? "was" : "were"} skipped.` : dropped.length > MAX_FILES ? `Up to ${MAX_FILES} resumes at a time — the rest were left out.` : null;
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState<Tab | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ["reqScans", reqId] });
    void qc.invalidateQueries({ queryKey: ["funnel", reqId] });
    void qc.invalidateQueries({ queryKey: ["requisitions"] });
  };

  const upload = useMutation({
    mutationFn: async () =>
      api.post<{ scored: number; unscored: number; duplicates: number }>(`/requisitions/${reqId}/resumes`, {
        resumes: await Promise.all(
          files.map(async (f) => ({
            filename: f.name,
            mime: f.type || "application/octet-stream",
            contentBase64: await toBase64(f),
          })),
        ),
      }),
    onSuccess: (res) => {
      refresh();
      setConfirm(null);
      setFiles([]);
      const parts = [`${res.scored} scored`];
      if (res.unscored > 0) parts.push(`${res.unscored} couldn’t be scored (not charged)`);
      if (res.duplicates > 0) parts.push(`${res.duplicates} already on file`);
      setDone(`Resumes uploaded — ${parts.join(", ")}. They appear under Resume Scanning.`);
    },
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<{ added: { candidacyId: string; emailed?: boolean }[] }>(`/requisitions/${reqId}/candidates`, {
        candidates: [{ name: name.trim(), email: email.trim() }],
      }),
    onSuccess: (res) => {
      refresh();
      setConfirm(null);
      const who = name.trim();
      const created = res.added?.[0];
      setName("");
      setEmail("");
      setDone(
        !created
          ? `${who} is already on this requirement — nothing was charged.`
          : created.emailed
            ? `${who} added and emailed their interview link.`
            : `${who} added. No email was sent — copy their link from the requirement page.`,
      );
    },
  });

  const pick = (e: ChangeEvent<HTMLInputElement>): void => {
    const chosen = Array.from(e.target.files ?? []);
    e.target.value = ""; // let the same file be picked again
    addFiles(chosen);
  };

  // Drop anywhere on the page while the modal is open (the scrim covers the whole viewport).
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const hasFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const onDragEnter = (e: DragEvent): void => {
    if (!hasFiles(e) || confirm) return;
    e.preventDefault();
    dragDepth.current++;
    setDragging(true);
  };
  const onDragOver = (e: DragEvent): void => {
    if (!hasFiles(e)) return;
    e.preventDefault(); // required, or the browser opens the file instead of dropping it
    if (e.dataTransfer) e.dataTransfer.dropEffect = confirm ? "none" : "copy";
  };
  const onDragLeave = (e: DragEvent): void => {
    if (!hasFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = (e: DragEvent): void => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (confirm || upload.isPending) return;
    setTab("upload");
    addFiles(Array.from(e.dataTransfer.files));
  };

  const addFiles = (chosen: File[]): void => {
    const tooBig = chosen.filter((f) => f.size > MAX_BYTES);
    const merged = [...files, ...chosen.filter((f) => f.size <= MAX_BYTES)]
      .filter((f, i, a) => a.findIndex((g) => g.name === f.name && g.size === f.size) === i)
      .slice(0, MAX_FILES);
    setFiles(merged);
    setDone(null);
    setFileNote(
      tooBig.length
        ? `${tooBig.length} file${tooBig.length === 1 ? " is" : "s are"} over 10 MB and ${tooBig.length === 1 ? "was" : "were"} skipped.`
        : files.length + chosen.length > MAX_FILES
          ? `Up to ${MAX_FILES} resumes at a time — the rest were left out.`
          : null,
    );
  };

  const submitDirect = (e: FormEvent): void => {
    e.preventDefault();
    if (name.trim() && email.trim()) setConfirm("direct");
  };

  const title = displayTitle(reqTitle);

  return (
    <>
      <div className="rn-modal-scrim" data-no-pan role="dialog" aria-modal="true" aria-labelledby="rn-add-title" onClick={onClose}
        onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
        {dragging && (
          <div className="rn-dropveil" aria-hidden="true">
            <UploadCloud size={44} />
            <b>Drop resumes to add them</b>
            <span>PDF, Word or text · up to {MAX_FILES} at a time</span>
          </div>
        )}
        <div className="rn-modal" onClick={(e) => e.stopPropagation()}>
          <header className="rn-modal-head">
            <div>
              <h2 id="rn-add-title">Add candidate</h2>
              <p>to <b>{title}</b></p>
            </div>
            <button type="button" className="rn-icon-btn" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </header>

          <nav className="rn-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "upload"} className={tab === "upload" ? "is-on" : ""} onClick={() => setTab("upload")}>
              <UploadCloud size={14} /> Upload CVs
            </button>
            <button type="button" role="tab" aria-selected={tab === "direct"} className={tab === "direct" ? "is-on" : ""} onClick={() => setTab("direct")}>
              <UserPlus size={14} /> Name &amp; email
            </button>
          </nav>

          <div className="rn-modal-body">
            {done && <div className="rn-modal-note ok">{done}</div>}

            {tab === "upload" ? (
              <>
                <button type="button" className="rn-drop" onClick={() => input.current?.click()}>
                  <UploadCloud size={28} />
                  <b>Choose or drag &amp; drop PDF, Word or text resumes</b>
                  <span>Up to {MAX_FILES} at a time, 10 MB each. Each one is scored against this requirement.</span>
                </button>
                <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={pick} />
                {fileNote && <div className="rn-modal-note warn">{fileNote}</div>}
                {files.length > 0 && (
                  <ul className="rn-files">
                    {files.map((f) => (
                      <li key={`${f.name}|${f.size}`}>
                        <FileText size={14} />
                        <span title={f.name}>{f.name}</span>
                        <small>{Math.max(1, Math.round(f.size / 1024))} KB</small>
                        <button type="button" onClick={() => setFiles(files.filter((g) => g !== f))} aria-label={`Remove ${f.name}`}>
                          <Trash2 size={13} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="rn-muted sm">The AI ranks resumes against the requirement; you decide who to interview.</p>
                <footer className="rn-modal-foot">
                  <button type="button" className="rn-tool wide" onClick={onClose}>Cancel</button>
                  <button type="button" className="rn-tour-primary" disabled={!files.length} onClick={() => setConfirm("upload")}>
                    Upload {files.length ? files.length : ""}
                  </button>
                </footer>
              </>
            ) : (
              <form onSubmit={submitDirect}>
                <div className="rn-fields">
                  <label>
                    <span>Full name</span>
                    <input value={name} onChange={(e) => (setName(e.target.value), setDone(null))} placeholder="e.g. Anjali Nair" required maxLength={200} />
                  </label>
                  <label>
                    <span>Email</span>
                    <input type="email" value={email} onChange={(e) => (setEmail(e.target.value), setDone(null))} placeholder="anjali@example.com" required maxLength={320} />
                  </label>
                </div>
                <p className="rn-muted sm">
                  No CV needed — they’re sent the AI interview link, with questions drawn from their own experience.
                  Nothing is scored until they interview.
                </p>
                <footer className="rn-modal-foot">
                  <button type="button" className="rn-tool wide" onClick={onClose}>Cancel</button>
                  <button type="submit" className="rn-tour-primary" disabled={!name.trim() || !email.trim()}>
                    Send interview
                  </button>
                </footer>
              </form>
            )}
          </div>
        </div>
      </div>

      {confirm === "upload" && (
        <ConfirmChargeModal
          title="Confirm & score resumes"
          subtitle={`${files.length} resume${files.length === 1 ? "" : "s"} will be scored against ${title}.`}
          unit="scan"
          count={files.length}
          lines={[]}
          confirmLabel={files.length === 1 ? "Score resume" : "Score resumes"}
          busy={upload.isPending}
          error={upload.isError ? serverMessage(upload.error, "Couldn’t upload those resumes — please try again.") : null}
          onConfirm={() => upload.mutate()}
          onCancel={() => !upload.isPending && (upload.reset(), setConfirm(null))}
        />
      )}
      {confirm === "direct" && (
        <ConfirmChargeModal
          title="Confirm & send interview"
          subtitle={`${name.trim()} will be invited to a verified AI interview for ${title}.`}
          unit="interview"
          count={1}
          lines={[]}
          confirmLabel="Send interview"
          busy={add.isPending}
          error={add.isError ? serverMessage(add.error, "Couldn’t add that candidate — check the email and try again.") : null}
          onConfirm={() => add.mutate()}
          onCancel={() => !add.isPending && (add.reset(), setConfirm(null))}
        />
      )}
    </>
  );
}
